import React, { useState, useEffect } from "react"
import { Link } from "gatsby"
import clsx from "clsx"
import { routes } from "../utils/routes"

interface HeaderProps {
  isHome?: boolean
}

const Header: React.FC<HeaderProps> = ({ isHome = false }) => {
  const [showOnScroll, setShowOnScroll] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [scrolledMenuOpen, setScrolledMenuOpen] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      setShowOnScroll(window.scrollY > 50)
    }

    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (menuOpen || scrolledMenuOpen) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = "unset"
    }

    return () => {
      document.body.style.overflow = "unset"
    }
  }, [menuOpen, scrolledMenuOpen])

  return (
    <header
      className={clsx(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
        showOnScroll ? "bg-[#131312] shadow-sm border-b border-primary" : "bg-[#131312] md:bg-transparent"
      )}
    >
      <div className={clsx(
        "container flex justify-between items-center pl-4 relative",
        showOnScroll ? "py-2 lg:py-4" : "py-2 lg:py-4",
        isHome && !showOnScroll && "lg:pt-16"
      )}>
        <div className={clsx(
          showOnScroll ? "w-[60px] md:w-[60px]" : "w-[60px] md:w-[125px]"
        )}>
          <Link to={routes.home()} title="RBAI Foundation" className="relative">
            <img 
              src="/images/Logo/Gilroy-logo.png" 
              alt="RBAI Foundation" 
              className="w-full h-auto md:hidden" 
            />
            <img 
              src="/images/Logo/Gilroy-logo.png" 
              alt="RBAI Foundation" 
              className="w-full h-auto hidden md:block" 
            />
          </Link>
        </div>
        <div className="flex items-center">
          {/* Desktop navigation - only visible when scrolled */}
          <nav
            className={clsx(
              showOnScroll ? "hidden lg:block lg:relative lg:bg-transparent lg:inset-auto lg:text-start" : "hidden"
            )}
          >
            <ul className="flex flex-col lg:flex-row items-center gap-8 text-[#bfbfbf] font-gilroy text-base font-semibold capitalize">
              <li className="relative group">
                <Link 
                  to={routes.socialMobility()} 
                  className="block py-2 hover:text-white transition-colors relative"
                >
                  Transformative Bursaries
                  <div className="absolute bottom-[-8px] left-0 right-0 h-[6px] bg-primary opacity-0 group-hover:opacity-100 transition-opacity"></div>
                </Link>
              </li>
              <li className="relative group">
                <Link 
                  to={routes.southWingDevelopment()} 
                  className="block py-2 hover:text-white transition-colors relative"
                >
                  The South Wing
                  <div className="absolute bottom-[-8px] left-0 right-0 h-[6px] bg-primary opacity-0 group-hover:opacity-100 transition-opacity"></div>
                </Link>
              </li>
              <li className="relative group">
                <Link 
                  to={routes.pledge()} 
                  className="block py-2 hover:text-white transition-colors relative"
                >
                  Sponsor A Paviour
                  <div className="absolute bottom-[-8px] left-0 right-0 h-[6px] bg-primary opacity-0 group-hover:opacity-100 transition-opacity"></div>
                </Link>
              </li>
              <li className="relative group">
                <Link 
                  to={routes.donate()} 
                  className="block py-2 text-primary hover:text-white transition-colors relative"
                >
                  Donate
                  <div className="absolute bottom-[-8px] left-0 right-0 h-[6px] bg-primary opacity-0 group-hover:opacity-100 transition-opacity"></div>
                </Link>
              </li>
            </ul>
          </nav>

          {/* Always-visible burger menu in right corner */}
          <div className="relative ml-4">
            <button
              type="button"
              title="Toggle Menu"
              className="flex items-center justify-center w-[50px] h-[50px] rounded-full transition-colors"
              onClick={() => setMenuOpen(!menuOpen)}
            >
              {menuOpen ? (
                <span className="flex items-center justify-center" aria-hidden="true">
                  <span className="block w-8 h-[4px] bg-primary rotate-45"></span>
                  <span className="block w-8 h-[4px] bg-primary -rotate-45"></span>
                </span>
              ) : (
                <span className="flex flex-col items-center justify-center" aria-hidden="true">
                  <span className="block w-8 h-[4px] bg-primary mb-1"></span>
                  <span className="block w-8 h-[4px] bg-primary mb-1"></span>
                  <span className="block w-8 h-[4px] bg-primary"></span>
                </span>
              )}
            </button>
            <div
              className={clsx(
                "fixed inset-0 bg-[#1f1f1f] z-[50] flex flex-col",
                menuOpen ? "flex" : "hidden"
              )}
            >
              {/* Close button aligned with menu content */}
              <div className="flex justify-end px-20 sm:px-[120px] pt-6">
                <button
                  type="button"
                  title="Close Menu"
                  className="flex items-center justify-center w-[50px] h-[50px] rounded-full transition-colors"
                  onClick={() => setMenuOpen(false)}
                >
                  <span className="flex items-center justify-center relative" aria-hidden="true">
                    <span className="block w-8 h-[4px] bg-primary rotate-45 absolute"></span>
                    <span className="block w-8 h-[4px] bg-primary -rotate-45 absolute"></span>
                  </span>
                </button>
              </div>
              
              {/* Menu items aligned to left */}
              <div className="flex-1 flex items-start justify-start px-20 sm:px-[120px] pt-8 overflow-y-auto">
                <ul
                  role="menu"
                  className="flex flex-col space-y-4 text-white font-gilroy text-[32px] sm:text-[32px] font-bold w-full"
                >
                  <li className="relative group w-full">
                    <Link 
                      className="block py-2 text-white transition-colors" 
                      title="FAQs" 
                      to={routes.faqs()}
                      onClick={() => setMenuOpen(false)}
                    >
                      FAQs
                    </Link>
                    <div className="absolute bottom-0 left-0 right-0 h-[4px] bg-primary"></div>
                  </li>
                  <li className="relative group w-full">
                    <Link 
                      className="block py-2 text-white transition-colors" 
                      title="Stay Connected" 
                      to={routes.stayConnected()}
                      onClick={() => setMenuOpen(false)}
                    >
                      Stay Connected
                    </Link>
                    <div className="absolute bottom-0 left-0 right-0 h-[4px] bg-primary"></div>
                  </li>
                  <li className="relative group w-full">
                    <Link 
                      className="block py-2 text-white transition-colors" 
                      title="Recognising your Gift" 
                      to={routes.recognisingYourGift()}
                      onClick={() => setMenuOpen(false)}
                    >
                      Recognising your Gift
                    </Link>
                    <div className="absolute bottom-0 left-0 right-0 h-[4px] bg-primary"></div>
                  </li>
                  <li className="relative group w-full">
                    <Link 
                      className="block py-2 text-white transition-colors" 
                      title="Tax Efficient Giving" 
                      to={routes.taxEfficientGiving()}
                      onClick={() => setMenuOpen(false)}
                    >
                      Tax Efficient Giving
                    </Link>
                    <div className="absolute bottom-0 left-0 right-0 h-[4px] bg-primary"></div>
                  </li>
                  <li className="relative group w-full">
                    <Link 
                      className="block py-2 text-white transition-colors" 
                      title="Legacies" 
                      to={routes.legacies()}
                      onClick={() => setMenuOpen(false)}
                    >
                      Legacies
                    </Link>
                    <div className="absolute bottom-0 left-0 right-0 h-[4px] bg-primary"></div>
                  </li>
                  <li className="relative group w-full">
                    <Link 
                      className="block py-2 text-white transition-colors" 
                      title="RBAI Together" 
                      to={routes.rbaiTogether()}
                      onClick={() => setMenuOpen(false)}
                    >
                      RBAI Together
                    </Link>
                    <div className="absolute bottom-0 left-0 right-0 h-[4px] bg-primary"></div>
                  </li>
                  <li className="relative group w-full">
                    <Link 
                      className="block py-2 text-white transition-colors" 
                      title="Rugby 150 Book" 
                      to={routes.rugby150()}
                      onClick={() => setMenuOpen(false)}
                    >
                      Rugby 150 Book
                    </Link>
                    <div className="absolute bottom-0 left-0 right-0 h-[4px] bg-primary"></div>
                  </li>
                  <li className="relative group w-full">
                    <Link 
                      className="block py-2 text-white transition-colors" 
                      title="Contact Us" 
                      to={routes.contactUs()}
                      onClick={() => setMenuOpen(false)}
                    >
                      Contact Us
                    </Link>
                    <div className="absolute bottom-0 left-0 right-0 h-[4px] bg-primary"></div>
                  </li>
                </ul>
              </div>
            </div>
          </div>


        </div>
      </div>
    </header>
  )
}

export default Header
